/// <amd-module name="WorkItemsHub/External/NewWorkItem/NewWorkItem" />

import * as React from "react";
import { IPivotBarAction } from "VSSUI/PivotBar";
import { WorkItemTypeColorAndIcons, IColorAndIcon, getNormalizedValue } from "External/NewWorkItem/WorkItemTypeColorAndIconsProvider";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageContext } from "VSS/Platform/Context";
import { INewWorkItemData } from "./INewWorkItemData";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { uniqueSort } from "Wit/Common/Utilities/Array";
import * as Resources from "WorkItemsHub/Resources";

export interface IActionOptions {
    /**
     * Callback when Add New Item is clicked
     */
    addNewItem: (
        pageContext: IVssPageContext,
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        item: IPivotBarAction,
        workItemTypeName: string
    ) => void;
    /**
     * Optional list of work item type names, when not provided shows all work item types in the project
     */
    workItemTypesNames?: string[];
    /**
     * True is the action should be in disabled state
     */
    disabled?: boolean;
    /**
     * True to make the pivot bar action important (default is true)
     */
    important?: boolean;
}

export class PivotBarActionHelper {
    public static readonly newWorkItemCommandKey = "new-work-item";

    /**
     * Gets the Pivot Bar Actions for New Work Item
     * @param options Options for the pivot bar action
     */
    public static getNewWorkItemPivotBarActions(pageContext: IVssPageContext, options: IActionOptions): IPivotBarAction[] {
        const dataProvider = new WorkItemTypeColorAndIcons();
        return PivotBarActionHelper._getWorkItemTypeMenuItems(pageContext, options, dataProvider);
    }

    /**
     * Gets Pivot Bar Action for New Item (New Work Item)
     * @param options Options for the pivot bar action
     */
    public static getNewWorkItemPivotBarAction(pageContext: IVssPageContext, options: IActionOptions): IPivotBarAction {
        const important = options.important == null ? true : options.important; // default is True
        const dataProvider = new WorkItemTypeColorAndIcons();
        const vssIconProps = {
            iconName: "bowtie-math-plus-light",
            iconType: VssIconType.bowtie
        };

        // Do not show drop down when there is only one work item type
        if (options.workItemTypesNames && options.workItemTypesNames.length === 1) {
            return {
                key: PivotBarActionHelper.newWorkItemCommandKey,
                disabled: options.disabled,
                name: Resources.NewWorkItemButtonText,
                iconProps: vssIconProps,
                important: important,
                onClick: (ev, item) => options.addNewItem(pageContext, ev, item, options.workItemTypesNames[0])
            };
        }

        return {
            key: PivotBarActionHelper.newWorkItemCommandKey,
            disabled: options.disabled,
            name: Resources.NewWorkItemButtonText,
            iconProps: vssIconProps,
            important: important,
            children: PivotBarActionHelper._getWorkItemTypeMenuItems(pageContext, options, dataProvider)
        };
    }

    private static _getWorkItemTypeMenuItems(
        pageContext: IVssPageContext,
        options: IActionOptions,
        dataProvider: WorkItemTypeColorAndIcons
    ): IPivotBarAction[] {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const workItemData = contributionService.getData<INewWorkItemData>("ms.vss-work-web.new-workitem-data-provider-non-cached");
        if (!workItemData || !workItemData.workItemTypes) {
            return [];
        }

        let types: string[];
        if (options.workItemTypesNames && options.workItemTypesNames.length > 0) {
            types = uniqueSort(options.workItemTypesNames, localeIgnoreCaseComparer);
        } else {
            types = uniqueSort(workItemData.workItemTypes, localeIgnoreCaseComparer);
        }

        return types.map((workItemTypeName: string) =>
            PivotBarActionHelper._createMenuItem(pageContext, options, dataProvider, workItemData, workItemTypeName)
        );
    }

    private static _createMenuItem(
        pageContext: IVssPageContext,
        options: IActionOptions,
        dataProvider: WorkItemTypeColorAndIcons,
        workItemData: INewWorkItemData,
        workItemTypeName: string
    ): IPivotBarAction {
        const colorAndIcon = PivotBarActionHelper._getWorkItemTypeColorAndIcon(dataProvider, workItemData, workItemTypeName);
        const vssIconProps: IVssIconProps = {
            iconName: colorAndIcon.icon,
            iconType: VssIconType.bowtie,
            style: {
                color: colorAndIcon.color
            }
        };

        const menuItem: IPivotBarAction = {
            key: workItemTypeName,
            disabled: options.disabled,
            name: workItemTypeName,
            iconProps: vssIconProps,
            onClick: (ev, item) => options.addNewItem(pageContext, ev, item, workItemTypeName)
        };

        return menuItem;
    }

    private static _getWorkItemTypeColorAndIcon(
        dataProvider: WorkItemTypeColorAndIcons,
        workItemData: INewWorkItemData,
        typeName: string
    ): IColorAndIcon {
        let result: IColorAndIcon;

        typeName = getNormalizedValue(typeName);
        if (typeName && workItemData.colorAndIcons) {
            const colorAndIcon = workItemData.colorAndIcons[typeName];
            if (colorAndIcon) {
                result = dataProvider.setColorAndIcon(typeName, colorAndIcon.color, colorAndIcon.icon);
            }
        }

        if (!result) {
            result = WorkItemTypeColorAndIcons.getDefault();
        }

        return result;
    }
}
